import { Injectable } from '@angular/core';

import { AccaBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { AccaBoostTokenProviderService } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-token-provider.service';
import { concatMap, debounceTime, distinctUntilChanged, filter, ignoreElements, map, tap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { BetslipType } from '../../../core/betslip-type';
import BetslipComboBetActions from '../../combo-bet/actions';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { selectHasComboPreventionForTypeFactory } from '../../validation/selectors';
import { RewardTokensActions } from '../actions';
import { selectTokensStateContext } from '../selectors';
import { getEligibleAccaBoost, getSelectedTokenForContext } from '../services/linear-reward-tokens.utils';
import { isAccaBoostToken } from '../services/reward-tokens.utils';

// Effects shared between Digital and Betstation
@Injectable()
export class RewardTokensSharedEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private accaBoostTokenProviderService: AccaBoostTokenProviderService,
        private accaBoostConfig: AccaBoostConfig,
    ) {}

    checkAccaBoostEligibilityOnEligibilityChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.setComboBetEligibility, BetslipTypesActions.setSelectedType),
            map(() => RewardTokensActions.checkAccaBoostEligibility()),
        );
    });

    checkAccaBoostEligibilityOnComboPreventionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.picksErrorsCheckFinished),
            concatLatestFrom(() => [this.store.select(selectHasComboPreventionForTypeFactory(BetslipType.Combo))]),
            distinctUntilChanged(
                ([, hasComboPreventionPrevious], [, hasComboPreventionCurrent]) => hasComboPreventionPrevious === hasComboPreventionCurrent,
            ),
            map(() => RewardTokensActions.checkAccaBoostEligibility()),
        );
    });

    checkAccaBoostEligibilityOnTokenChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipComboBetActions.setComboBetRewardToken),
            filter((action) => !action.ignoreAccaBoost),
            map(() => RewardTokensActions.checkAccaBoostEligibility()),
        );
    });

    autoSelectAccaBoostTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.checkAccaBoostEligibility, BetslipErrorsActions.picksErrorsCheckFinished),
            concatLatestFrom(() => [
                this.store.select(selectTokensStateContext),
                this.store.select(selectIsLinearBetslip),
                this.store.select(betslipCurrentTypeSelector),
                this.store.select(selectHasComboPreventionForTypeFactory(BetslipType.Combo)),
            ]),
            filter(([, , isLinear, currentSelectedType]) => isLinear || currentSelectedType === BetslipType.Combo),
            // Debounce to avoid triggering this effect twice when elgibilities change and errors update
            debounceTime(2),
            concatMap(function* ([_, { tokens, eligibilityState, types }, , , hasComboPrevention]): Iterable<Action> {
                const availableAccaBoostToken = getEligibleAccaBoost(eligibilityState, tokens);

                const selectedToken = getSelectedTokenForContext({ betslipType: BetslipType.Combo }, tokens, types);

                if (isAccaBoostToken(selectedToken)) {
                    // current acca boost token is not eligible
                    if (!availableAccaBoostToken || hasComboPrevention) {
                        yield BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null, ignoreAccaBoost: true });
                    }
                    // there's another acca boost token with higher priority
                    else if (selectedToken.userTokenId !== availableAccaBoostToken.userTokenId) {
                        yield BetslipComboBetActions.setComboBetRewardToken({
                            rewardTokenId: availableAccaBoostToken.userTokenId,
                            ignoreAccaBoost: true,
                        });
                    }
                }

                // when no other token is selected and there's available acca boost token
                if (!selectedToken && availableAccaBoostToken && !hasComboPrevention) {
                    yield BetslipComboBetActions.setComboBetRewardToken({
                        rewardTokenId: availableAccaBoostToken.userTokenId,
                        ignoreAccaBoost: true,
                    });
                }
            }),
        );
    });

    triggerAccaBoostRefresh$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.triggerAccaBoostRefresh),
                filter(() => this.accaBoostConfig.isEnabled),
                tap(() => this.accaBoostTokenProviderService.refresh()),
                ignoreElements(),
            );
        },
        { dispatch: false },
    );
}
